import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MicButton } from "../pages/presentations/PresMedia";
import { useAuth } from "@clerk/react";
import { ArrowUp, Bot, Check, FileText, Loader2, Paperclip, Sparkles, X } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { API, errMsg, money } from "../lib/api";
import { uploadFile, FILE_STATUS, ACCEPT } from "../lib/upload";

const STARTERS = [
  "I want a website where customers upload drawings and get quantity estimates",
  "I run a small business and need a simple website with enquiries",
  "I want to sell products online with delivery",
];

const rich = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((s, i) => (s.startsWith("**") && s.endsWith("**") ? <strong key={i}>{s.slice(2, -2)}</strong> : s));

function TierSwitch({ c, m, onTier }) {
  const [busy, setBusy] = useState(null);
  if (!c.tiers?.length || c.removed) return null;
  const pick = async (k) => { if (k === c.tier_key || busy) return; setBusy(k); try { await onTier(c.key, k); } finally { setBusy(null); } };
  return (
    <div className="mt-2 grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={`Level for ${c.name}`} data-testid={`chat-tier-switch-${c.key}`}>
      {c.tiers.map((t) => {
        const on = t.key === c.tier_key;
        return (
          <button key={t.key} role="radio" aria-checked={on} onClick={() => pick(t.key)} disabled={!!busy} title={t.limits}
            className={`rounded-lg border px-2 py-1.5 text-left transition-colors disabled:opacity-60 ${on ? "border-[#0b3d2e] bg-[#0b3d2e] text-[#f6ecd2]" : "border-[#0b3d2e]/15 bg-white hover:border-[#0b3d2e]/40"}`} data-testid={`chat-tier-${c.key}-${t.key}`}>
            <span className="block text-[11px] font-semibold">{busy === t.key ? <Loader2 className="w-3 h-3 animate-spin inline" /> : t.label}</span>
            <span className={`block text-[11px] tabular-nums ${on ? "text-[#f6ecd2]/80" : "text-slate-500"}`}>{m(t.one_time)}{t.recurring > 0 ? ` + ${m(t.recurring)}/${c.interval || "mo"}` : ""}</span>
          </button>);
      })}
    </div>);
}

function CompareLevels({ c, api, pid, onTier }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const show = async () => {
    setOpen(true); setD(null);
    try { setD((await api.get(`/projects/${pid}/chat/levels`, { params: { key: c.key } })).data); } catch (e) { toast.error(errMsg(e)); setOpen(false); }
  };
  const pick = async (k) => { setBusy(k); try { await onTier(c.key, k); setOpen(false); } finally { setBusy(null); } };
  const m = (v) => (d ? money(v, d.symbol, d.decimals) : "");
  return (
    <>
      <button onClick={show} className="text-[11px] font-semibold text-[#0b3d2e] underline underline-offset-2 hover:text-[#9a7322]" data-testid={`chat-compare-${c.key}`}>What's the difference?</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="compare-levels-dialog">
          <DialogTitle className="text-2xl text-[#062a20]">{c.name}: compare levels</DialogTitle>
          <DialogDescription>Fixed prices from Altiora's pricing. Pick the level that fits — you can change it any time before paying the advance.</DialogDescription>
          {!d ? <p className="text-sm text-slate-500 py-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading levels…</p> : (
            <>
              <div className={`grid gap-4 mt-2 ${d.tiers.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                {d.tiers.map((t) => {
                  const on = t.key === d.tier_key;
                  return (
                    <div key={t.key} className={`rounded-2xl border p-5 flex flex-col ${on ? "border-[#0b3d2e] ring-1 ring-[#0b3d2e] bg-[#f3f8f5]" : "border-[#0b3d2e]/15 bg-white"}`} data-testid={`compare-col-${t.key}`}>
                      <div className="flex items-center justify-between"><p className="font-semibold text-[#062a20]">{t.label}</p>{on && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-[#0b3d2e] text-[#f6ecd2]">Current</span>}</div>
                      <p className="mt-2 text-xl font-semibold text-[#062a20] tabular-nums">{m(t.one_time)}</p>
                      <p className="text-xs text-slate-500">{t.recurring > 0 ? `+ ${m(t.recurring)} / ${d.interval || "month"}` : "No recurring charge"} · taxes included</p>
                      <p className="mt-4 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">What's included</p>
                      <p className="mt-1 text-sm text-slate-700 leading-relaxed">{t.scope}</p>
                      <p className="mt-4 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Maximum scope</p>
                      <p className="mt-1 text-sm text-slate-700">{t.limits || "—"}</p>
                      <div className="mt-auto pt-5">
                        {on ? <p className="text-xs text-emerald-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> On your project</p>
                          : <button onClick={() => pick(t.key)} disabled={!!busy} className="btn-emerald w-full !py-2" data-testid={`compare-choose-${t.key}`}>{busy === t.key ? <Loader2 className="w-4 h-4 animate-spin" /> : `Choose ${t.label}`}</button>}
                      </div>
                    </div>);
                })}
              </div>
              {(d.template.capabilities.length > 0 || d.template.limitations.length > 0) && (
                <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
                  {d.template.capabilities.length > 0 && <div><p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">What this feature can do</p><ul className="mt-1 list-disc pl-5 text-slate-700 space-y-0.5">{d.template.capabilities.map((x) => <li key={x}>{x}</li>)}</ul></div>}
                  {d.template.limitations.length > 0 && <div><p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Good to know (all levels)</p><ul className="mt-1 list-disc pl-5 text-slate-700 space-y-0.5">{d.template.limitations.map((x) => <li key={x}>{x}</li>)}</ul></div>}
                </div>)}
            </>)}
        </DialogContent>
      </Dialog>
    </>);
}

function PriceCards({ pr, onUndo, onTier, api, pid }) {
  const [busy, setBusy] = useState(null);
  if (!pr?.cards?.length) return null;
  const m = (v) => money(v, pr.symbol, pr.decimals);
  const undo = async (k) => { setBusy(k); try { await onUndo(k); } finally { setBusy(null); } };
  return (
    <div className="mt-3 space-y-2" data-testid="chat-price-cards">
      {pr.cards.map((c) => (
        <div key={c.key} className={`rounded-xl border p-3 transition-opacity ${c.removed ? "border-slate-200 bg-slate-50 opacity-70" : "border-[#c9a13b]/40 bg-gradient-to-br from-[#fffaf0] to-white"}`} data-testid={`chat-price-card-${c.key}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0"><p className={`font-semibold text-[#062a20] text-[13px] ${c.removed ? "line-through" : ""}`}>{c.name}</p>
              {c.tier && <p className="text-[11px] text-[#7a5a15]">Custom · {c.tier}{c.limits ? ` · ${c.limits}` : ""}</p>}</div>
            <div className="text-right shrink-0 tabular-nums">
              <p className={`font-semibold text-[#062a20] ${c.removed ? "line-through" : ""}`}>{c.one_time ? m(c.one_time) : "No extra cost"}</p>
              {c.recurring > 0 && <p className="text-[11px] text-slate-500">+ {m(c.recurring)} / {c.interval}</p>}
            </div>
          </div>
          <TierSwitch c={c} m={m} onTier={onTier} />
          <div className="mt-2 flex items-center justify-between gap-2">
            {c.tiers?.length > 0 && !c.removed ? <CompareLevels c={c} api={api} pid={pid} onTier={onTier} /> : <span />}
            {c.removed ? <span className="text-[11px] text-slate-500" data-testid={`chat-card-removed-${c.key}`}>Removed from your project</span>
              : <button onClick={() => undo(c.key)} disabled={busy === c.key} className="text-[11px] font-semibold text-red-700 hover:bg-red-50 rounded-full px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`chat-card-remove-${c.key}`}>
                {busy === c.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />} Remove</button>}
          </div>
        </div>))}
      {pr.project_total != null && <p className="text-[11px] text-slate-500 px-1" data-testid="chat-price-total">{pr.cards.some((c) => c.removed) ? "Package total when these were added" : `New ${({ essential: "Essential", recommended: "Recommended", best: "Best Possible", landing: "Landing Page" })[pr.package] || pr.package} package total`}: <b className="text-[#062a20]">{m(pr.project_total)}</b> (fixed price, before any coupon)</p>}
    </div>);
}

function Bubble({ m, onUndo, onTier, api, pid }) {
  const mine = m.role === "user";
  return (
    <div className={`flex gap-3 chat-in ${mine ? "justify-end" : ""}`} data-testid={`chat-msg-${m.role}`}>
      {!mine && <span className="mt-1 shrink-0 w-7 h-7 rounded-full bg-[#0b3d2e] text-[#f6ecd2] grid place-items-center"><Bot className="w-4 h-4" /></span>}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${mine ? "bg-[#0b3d2e] text-[#f6ecd2] rounded-br-sm" : "bg-white border border-[#0b3d2e]/10 text-slate-800 rounded-bl-sm shadow-sm"}`}>
        {m.content ? rich(m.content) : <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</span>}
        {m.files?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{m.files.map((f) => <span key={f.id} className="text-[11px] px-2 py-0.5 rounded-full bg-white/15 inline-flex items-center gap-1"><FileText className="w-3 h-3" />{f.filename}</span>)}</div>}
        {m.applied?.length > 0 && <ul className="mt-3 space-y-1 border-t pt-2" data-testid="chat-applied">{m.applied.map((a) => <li key={a} className="text-xs text-emerald-800 flex gap-1.5"><Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a}</li>)}</ul>}
        <PriceCards pr={m.pricing} onUndo={(k) => onUndo(m.id, k)} onTier={(k, t) => onTier(m.id, k, t)} api={api} pid={pid} />
      </div>
    </div>
  );
}

function AttachChips({ items, remove }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-3 pt-3">
      {items.map((f) => {
        const [label, cls] = FILE_STATUS[f.status] || [f.status, ""];
        return (
          <span key={f.key} className="text-xs bg-[#f4efe2] rounded-full pl-3 pr-1 py-1 inline-flex items-center gap-2" data-testid="chat-attachment">
            <FileText className="w-3.5 h-3.5 text-[#0b3d2e]" /><span className="max-w-[140px] truncate">{f.filename}</span>
            <span className={cls}>{f.status === "uploading" ? `${f.progress || 0}%` : label}</span>
            <button onClick={() => remove(f.key)} aria-label="Remove attachment" className="p-0.5 rounded-full hover:bg-white"><X className="w-3 h-3" /></button>
          </span>);
      })}
    </div>
  );
}

export default function AIChat({ project, api, onChange, className = "" }) {
  const { getToken } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [atts, setAtts] = useState([]);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const pid = project.id;

  useEffect(() => { api.get(`/projects/${pid}/chat`).then((r) => setMsgs(r.data.messages)).catch(() => setMsgs([])); }, [api, pid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const pending = atts.some((a) => ["uploading", "processing"].includes(a.status));
  useEffect(() => {
    if (!atts.some((a) => a.status === "processing")) return undefined;
    const t = setInterval(async () => {
      const r = await api.get(`/projects/${pid}/files`);
      setAtts((cur) => cur.map((a) => { const f = r.data.files.find((x) => x.id === a.id); return f ? { ...a, status: f.status, note: f.note } : a; }));
    }, 2500);
    return () => clearInterval(t);
  }, [atts, api, pid]);

  const attach = async (fileList) => {
    for (const file of Array.from(fileList)) {
      const key = `${file.name}-${Date.now()}`;
      setAtts((a) => [...a, { key, filename: file.name, status: "uploading", progress: 0 }]);
      try {
        const f = await uploadFile(api, pid, file, (p) => setAtts((a) => a.map((x) => (x.key === key ? { ...x, progress: p } : x))));
        setAtts((a) => a.map((x) => (x.key === key ? { ...x, id: f.id, status: f.status } : x)));
      } catch (e) { toast.error(errMsg(e)); setAtts((a) => a.filter((x) => x.key !== key)); }
    }
  };

  const send = useCallback(async (msg) => {
    const body = (msg ?? text).trim();
    if (!body || busy) return;
    const files = atts.filter((a) => a.id).map((a) => ({ id: a.id, filename: a.filename }));
    setText(""); setAtts([]); setBusy(true);
    setMsgs((m) => [...(m || []), { id: `u${Date.now()}`, role: "user", content: body, files }, { id: "live", role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/projects/${pid}/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` }, body: JSON.stringify({ message: body, file_ids: files.map((f) => f.id) }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "The assistant couldn't respond. Please try again.");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.t === "delta") setMsgs((m) => m.map((x) => (x.id === "live" ? { ...x, content: x.content + ev.c } : x)));
          if (ev.t === "done") setMsgs((m) => m.map((x) => (x.id === "live" ? ev.message : x)));
        }
      }
      onChange?.();
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.filter((x) => x.id !== "live"));
    } finally { setBusy(false); }
  }, [text, busy, atts, pid, getToken, onChange]);

  const undo = async (mid, key) => {
    try {
      await api.post(`/projects/${pid}/chat/undo`, { message_id: mid, key });
      setMsgs((ms) => ms.map((x) => (x.id === mid ? { ...x, pricing: { ...x.pricing, cards: x.pricing.cards.map((c) => (c.key === key ? { ...c, removed: true } : c)) } } : x)));
      toast.success("Removed from your project");
      onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const tier = async (mid, key, t) => {
    try {
      const r = await api.post(`/projects/${pid}/chat/tier`, { message_id: mid, key, tier: t });
      setMsgs((ms) => ms.map((x) => (x.id === mid ? { ...x, pricing: { ...x.pricing, project_total: r.data.project_total, cards: x.pricing.cards.map((c) => (c.key === key ? r.data.card : c)) } } : x)));
      toast.success(`Switched to ${r.data.card.tier}`);
      onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const last = msgs?.filter((m) => m.role === "assistant").slice(-1)[0];
  return (
    <div className={`flex flex-col bg-[#fbf9f3] rounded-2xl border border-[#0b3d2e]/10 overflow-hidden ${className}`} data-testid="ai-chat">
      <div className="px-5 py-4 border-b bg-white flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0b3d2e] to-[#14583f] text-[#f6ecd2] grid place-items-center"><Sparkles className="w-4 h-4" /></span>
        <div><p className="font-semibold text-[#062a20] text-sm">Altiora AI assistant</p><p className="text-xs text-slate-500">Describe what you need — I'll ask what matters and build your scope.</p></div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-[260px]" data-testid="chat-messages">
        {msgs === null && <p className="text-sm text-slate-500">Loading conversation…</p>}
        {msgs?.length === 0 && (
          <div className="space-y-3" data-testid="chat-empty">
            <p className="text-sm text-slate-600">Tell me about your business and what the website should do — in your own words. You can attach drawings, documents, spreadsheets or images.</p>
            <div className="flex flex-col gap-2">{STARTERS.map((s, i) => <button key={s} onClick={() => send(s)} className="chip text-left" data-testid={`chat-starter-${i}`}>{s}</button>)}</div>
          </div>)}
        {msgs?.map((m) => <Bubble key={m.id} m={m} onUndo={undo} onTier={tier} api={api} pid={pid} />)}
        <div ref={endRef} />
      </div>
      {!busy && last?.suggestions?.length > 0 && <div className="px-5 pb-2 flex flex-wrap gap-2" data-testid="chat-suggestions">{last.suggestions.map((s, i) => <button key={s} onClick={() => send(s)} className="chip" data-testid={`chat-suggestion-${i}`}>{s}</button>)}</div>}
      <div className="border-t bg-white">
        <AttachChips items={atts} remove={(k) => setAtts((a) => a.filter((x) => x.key !== k))} />
        <form className="flex items-end gap-2 p-3" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { attach(e.target.files); e.target.value = ""; }} data-testid="chat-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="p-2.5 rounded-full hover:bg-[#f4efe2] text-[#0b3d2e]" aria-label="Attach files" data-testid="chat-attach-btn"><Paperclip className="w-5 h-5" /></button>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Type your requirement or question…" className="flex-1 min-h-[44px] max-h-40 resize-none rounded-xl" data-testid="chat-input" aria-label="Message the AI assistant"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <MicButton testid="chat-mic" onText={(t) => setText((x) => `${x ? `${x} ` : ""}${t}`)} />
          <button disabled={busy || pending || !text.trim()} className="w-11 h-11 rounded-full bg-[#0b3d2e] text-[#f6ecd2] grid place-items-center disabled:opacity-40 transition-transform hover:scale-105" aria-label="Send" data-testid="chat-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}</button>
        </form>
        {pending && <p className="px-4 pb-2 text-[11px] text-slate-500">Waiting for your files to finish uploading and reading…</p>}
      </div>
    </div>
  );
}
